import { useState } from "react";

export const landmarks = [
  { name: "Orlando International Airport (MCO)", icon: "ri-flight-takeoff-line", distance: "~10 miles southeast of downtown", desc: "Primary international airport for Central Florida — 50M+ annual passengers. Major hub for Southwest, Spirit, Frontier, and JetBlue with extensive domestic and international routes to the Caribbean, Latin America, and Europe.", url: "https://www.orlandoairports.net/", schemaType: "Airport" },
  { name: "Walt Disney World Resort", icon: "ri-landscape-line", distance: "~20 miles southwest — Lake Buena Vista", desc: "The world's most-visited vacation resort with four theme parks, two water parks, and 25+ resort hotels. Draws 58M+ annual visitors making Orlando the most-visited destination in the U.S.", url: "https://www.disneyworld.com/", schemaType: "TouristAttraction" },
  { name: "Universal Orlando Resort", icon: "ri-landscape-line", distance: "~10 miles southwest of downtown", desc: "Major theme park resort with Universal Studios Florida, Islands of Adventure, Volcano Bay water park, and the Wizarding World of Harry Potter. A top driver of Orlando's international tourism.", url: "https://www.universalorlando.com/", schemaType: "TouristAttraction" },
  { name: "Orange County Convention Center", icon: "ri-building-line", distance: "International Drive — ~10 miles south", desc: "Second-largest convention center in the U.S. hosting 200+ events annually with 1.4M attendees. Major driver of business travel — many international conferences require valid passports for overseas attendees.", url: "https://www.occc.net/", schemaType: "TouristAttraction" },
  { name: "SeaWorld Orlando", icon: "ri-landscape-line", distance: "~13 miles south of downtown — International Drive", desc: "Major marine-life theme park and entertainment destination. Combined with Aquatica water park and Discovery Cove, it draws millions of domestic and international visitors annually.", url: "https://seaworld.com/orlando/", schemaType: "TouristAttraction" },
  { name: "Orlando Sanford Intl Airport (SFB)", icon: "ri-flight-takeoff-line", distance: "~30 miles north — Sanford, FL", desc: "Secondary airport serving Allegiant Air and select international charter flights to the UK and Europe. Popular alternative for budget-conscious Orlando travelers heading to secondary markets.", url: "https://www.orlandosanfordairport.com/", schemaType: "Airport" },
  { name: "Lake Eola Park", icon: "ri-landscape-line", distance: "Downtown Orlando", desc: "Iconic 43-acre park in the heart of downtown Orlando featuring a swan-filled lake, amphitheater, and walking paths. A defining landmark of Orlando's urban core and community gathering place.", url: "https://www.orlando.gov/", schemaType: "TouristAttraction" },
  { name: "ICON Park", icon: "ri-store-2-line", distance: "International Drive — ~10 miles south", desc: "20-acre entertainment complex on International Drive featuring The Wheel observation attraction, Madame Tussauds, SEA LIFE Aquarium, and 40+ restaurants and shops. A major tourist destination.", url: "https://iconparkorlando.com/", schemaType: "TouristAttraction" },
];

const photoLocations = [
  { name: "CVS Pharmacy — S Orange Ave", address: "1 S Orange Ave, Orlando, FL 32801", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — E Colonial Dr", address: "2301 E Colonial Dr, Orlando, FL 32803", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — S Semoran Blvd", address: "4800 S Semoran Blvd, Orlando, FL 32822", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — W Colonial Dr", address: "2400 W Colonial Dr, Orlando, FL 32804", hours: "Call for hours" },
  { name: "Walgreens — E Colonial Dr (24 hrs)", address: "3302 E Colonial Dr, Orlando, FL 32803", hours: "Open 24 Hours" },
  { name: "Walgreens — S Orange Ave (24 hrs)", address: "4600 S Orange Ave, Orlando, FL 32806", hours: "Open 24 Hours" },
  { name: "The UPS Store — Sand Lake Rd", address: "7345 W Sand Lake Rd, Ste 300, Orlando, FL 32819", hours: "Call for hours" },
  { name: "The UPS Store — E Colonial Dr", address: "2431 E Colonial Dr, Orlando, FL 32803", hours: "Call for hours" },
  { name: "FedEx Office — S Orange Ave", address: "1 S Orange Ave, Orlando, FL 32801", hours: "Call for hours" },
  { name: "FedEx Office — International Dr", address: "8001 S International Dr, Orlando, FL 32819", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Orange County Tax Collector — Millenia",
    address: "4415 Millenia Plaza Way, Orlando, FL 32839",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    featured: true,
  },
  {
    name: "Orange County Tax Collector — Pine Hills",
    address: "3841 W Colonial Dr, Orlando, FL 32808",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "West Orlando option. Call ahead for current wait times.",
    featured: false,
  },
  {
    name: "Orange County Tax Collector — East Orlando",
    address: "3038 N Goldenrod Rd, Orlando, FL 32807",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "East side option. Appointment recommended.",
    featured: false,
  },
];

export default function OrlandoHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Orlando Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Orlando resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in Orlando</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations in Orlando include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Orange County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Orange County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Orange County. Online, mail, or in-person options available.", url: "https://orange.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Orange County. Required for estate and legal matters.", url: "https://orange.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Orange County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.myorangeclerk.com/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Orange County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.myorangeclerk.com/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Orange County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Orange County Tax Collector — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Orange County Tax Collector handles DMV services in the Orlando area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.octaxcol.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Orange County Tax Collector — DMV Services
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Orange County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Orlando Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Orlando is served by two major airports. If your trip is coming up fast, make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "MCO",
                    name: "Orlando International Airport",
                    distance: "Primary airport — serving all of Central Florida",
                    address: "One Airport Blvd, Orlando, FL 32827",
                    note: "Major hub for American, Delta, Southwest, United, JetBlue & international carriers.",
                    flightUrl: "https://www.orlandoairports.net/",
                    flightLabel: "MCO Airport Website",
                    featured: true,
                  },
                  {
                    code: "SFB",
                    name: "Orlando Sanford International Airport",
                    distance: "~30 miles north of downtown Orlando",
                    address: "1200 Red Cleveland Blvd, Sanford, FL 32773",
                    note: "Smaller airport serving select domestic and charter routes.",
                    flightUrl: "https://www.orlandosanfordairport.com/",
                    flightLabel: "SFB Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near Orlando</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Orlando is the most-visited destination in the United States, anchored by world-famous theme parks and a massive convention industry. These landmarks and transportation hubs define travel patterns for Central Florida — from the 50M+ passengers at MCO to the 58M+ annual visitors at Walt Disney World.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are departing from MCO on an international flight, attending a conference at the Orange County Convention Center that requires overseas travel, or heading to Universal or Disney for an international vacation — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for Orlando residents.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}